import { Link } from '@tanstack/react-router'
import { ArrowUpRight, Briefcase, GraduationCap, PackageCheck, Trophy, Users } from 'lucide-react'

import { cn } from '@/lib/cn'
import { formatRupiah } from '@/lib/format'
import type { AttachmentKind, PostAttachment } from '@/features/sosial/api'

/**
 * Setiap jenis punya warna dan lambangnya sendiri supaya pembaca mengenali
 * apa yang ditawarkan sebelum sempat membaca judulnya.
 */
const rupa: Record<AttachmentKind, { icon: React.ReactNode; pil: string }> = {
  service: { icon: <PackageCheck className="size-3.5" />, pil: 'bg-jasa text-arang-900' },
  course: { icon: <GraduationCap className="size-3.5" />, pil: 'bg-kelas text-arang-900' },
  vacancy: { icon: <Briefcase className="size-3.5" />, pil: 'bg-lowongan text-arang-900' },
  contest: { icon: <Trophy className="size-3.5" />, pil: 'bg-kuning-500 text-arang-900' },
  job: { icon: <Users className="size-3.5" />, pil: 'bg-proyek text-arang-900' },
}

/**
 * AttachmentCard menampilkan produk atau jasa yang dilampirkan pada kiriman.
 *
 * Inilah yang membuat lini masa terbaca sekilas: judul, jenis, dan harganya
 * langsung terlihat tanpa pembaca perlu menebak dari kalimat pengantarnya.
 */
export function AttachmentCard({
  lampiran,
  className,
  onRemove,
  sebagai = 'tautan',
}: {
  lampiran: PostAttachment
  className?: string
  /** Bila diisi, kartu menampilkan tombol lepas dan tidak menjadi tautan. */
  onRemove?: () => void
  /**
   * Bentuk kartunya. "polos" dipakai ketika kartu berada di dalam elemen
   * yang sudah dapat diklik — tautan di dalam tombol bukan HTML yang sah,
   * dan pada peramban ia justru berpindah halaman alih-alih memilih.
   */
  sebagai?: 'tautan' | 'polos'
}) {
  const gaya = rupa[lampiran.kind] ?? rupa.service

  const isi = (
    <div
      className={cn(
        'flex items-center gap-3 rounded-xl border border-line bg-raised p-2.5 transition',
        onRemove ? '' : 'hover:border-ink-subtle hover:bg-sunken',
        className,
      )}
    >
      {lampiran.image_url ? (
        <img
          src={lampiran.image_url}
          alt=""
          loading="lazy"
          className="size-14 shrink-0 rounded-lg bg-sunken object-cover"
        />
      ) : (
        <span className={cn('grid size-14 shrink-0 place-items-center rounded-lg', gaya.pil)}>
          {gaya.icon}
        </span>
      )}

      <div className="min-w-0 flex-1">
        <span
          className={cn(
            'mono-label inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px]',
            gaya.pil,
          )}
        >
          {gaya.icon}
          {lampiran.kind_label}
        </span>

        <p className="mt-1 truncate font-bold text-ink">{lampiran.title}</p>

        <p className="mono-label truncate text-[10px] text-subtle">
          {lampiran.price > 0 ? (
            <>
              {lampiran.price_label ? `${lampiran.price_label} ` : ''}
              <span className="text-brand">{formatRupiah(lampiran.price)}</span>
            </>
          ) : (
            /* Harga nol bukan berarti gratis pada setiap jenis — lowongan
               yang gajinya dirahasiakan juga bernilai nol. Yang ditampilkan
               kategorinya, bukan angka yang menyesatkan. */
            lampiran.subtitle
          )}
        </p>
      </div>

      {onRemove ? (
        <button
          type="button"
          onClick={onRemove}
          className="mono-label shrink-0 rounded-full px-2.5 py-1 text-[10px] text-muted transition hover:bg-sunken hover:text-danger"
        >
          Lepas
        </button>
      ) : (
        <ArrowUpRight className="size-4 shrink-0 text-subtle" />
      )}
    </div>
  )

  if (onRemove || sebagai === 'polos') return isi

  return (
    <Link to={lampiran.href} className="block">
      {isi}
    </Link>
  )
}
